/**
 * O aviso do topo de Dados (docs/spec/settings.md): o navegador não desenha 3D. Mostra o motivo que
 * ele deu (dice/no3dHelp.ts), os passos para ligar a aceleração gráfica no navegador desta pessoa, a
 * mensagem dele para quem pedir ajuda e o link para a ajuda. Some sozinho quando o 3D volta: a
 * conferência roda de novo a cada página aberta, e a aceleração só vale com o navegador reaberto.
 */
import { docsUrl } from '../docs.js';
import { useLanguage, useT } from '../i18n/index.js';
import { browserFamily, isMac, NO3D_REASON_KEY, no3dSteps, type No3DStep } from '../dice/no3dHelp.js';
import { webglStatus } from '../dice/sceneGuard.js';

/** O texto do passo, com o nome da opção em negrito no lugar do `{option}`. */
function StepText({ step }: { step: No3DStep }) {
  const t = useT();
  if (!step.option) return <>{t(step.key, step.params)}</>;
  const [before, after = ''] = t(step.key, { ...step.params, option: '\u0000' }).split('\u0000');
  return (
    <>
      {before}
      <strong>{t(step.option)}</strong>
      {after}
    </>
  );
}

export function No3DBanner() {
  const t = useT();
  const language = useLanguage((s) => s.language);
  const status = webglStatus();
  if (status.ok) return null;
  const ua = navigator.userAgent;
  // sem WebGL por política, ligar a aceleração não resolve: não há passos
  const steps = status.reason === 'policy' ? [] : no3dSteps(browserFamily(ua), isMac(ua));
  return (
    <div className="rd-prefs__banner rd-prefs__no3d" role="status">
      <div className="rd-prefs__bannerText">
        <strong>{t('dice.no3d.title')}</strong>
        <span>{t(`dice.no3d.why.${NO3D_REASON_KEY[status.reason]}`)}</span>
        {steps.length ? (
          <ol className="rd-prefs__no3dSteps">
            {steps.map((step) => (
              <li key={step.key}>
                <StepText step={step} />
              </li>
            ))}
          </ol>
        ) : null}
        {status.message ? (
          <details className="rd-prefs__no3dDetails">
            <summary>{t('dice.no3d.details')}</summary>
            <code>{status.message}</code>
          </details>
        ) : null}
      </div>
      <a className="rd-btn rd-btn--secondary" href={`${docsUrl('ajuda/problemas', language)}#${t('dice.no3d.helpAnchor')}`} target="_blank" rel="noreferrer noopener">
        {t('dice.no3d.help')}
      </a>
    </div>
  );
}
